import moment from 'moment';
import { StatusCodes } from 'http-status-codes';

import ApiService from 'services/ApiService/ApiService';
import parseCatch from 'utils/parseCatch';
import { User } from 'stores/UserStore/UserStore.types';
import { ENUM_IDENTITY_STATUS } from 'enum/profile';
import TracingService from 'services/TracingService';
import ConfigurationService, { ConfigurationKeys } from 'services/ConfigurationService';
import logger from 'logger';


type ImageType = {
  imageType: string;
  dateAdded: number;
}

type IdentityImages = {
  CAPTURE: ImageType;
  BACK: ImageType;
  FRONT: ImageType;
}

export type Identity = {
  aliases: string;
  country: string;
  dateAdded: number;
  dateModified: number;
  identityType: string;
  images: IdentityImages;
  name: string;
  status: ENUM_IDENTITY_STATUS;
  username: User['username'];
}

const BASE_URL = ConfigurationService.get(ConfigurationKeys.BASE_URL);
const v1 = '/rest/v1.0';

const endpoints = {
  'image_upload': `${BASE_URL}${v1}/accounts/{performerName}/identities/{identityType}/images`,
  create: `${BASE_URL}${v1}/accounts/{performerName}/identities`,
  update: `${BASE_URL}${v1}/accounts/{performerName}/identities/{identityType}`,
  get: `${BASE_URL}${v1}/accounts/{username}/identities`,
};

class IdentitiesService {
  getIdentities(username: User['username']) {
    const url = ApiService.substitute(endpoints.get, { username: encodeURIComponent(username) });

    return ApiService.get(url)
      .then((response) => response.data)
      .catch((error) => {
        if (error.status === StatusCodes.NOT_FOUND) {
          logger.warn(`User "${username}" doesn't have identities`);
        } else {
          parseCatch(error);
        }
      });
  }

  updateIdentity(
    username: Identity['username'],
    identityType: Identity['identityType'],
    data: {
      name: string;
      birthdate: moment.Moment;
      aliases?: string;
    }
  ) {
    const headers = TracingService.generateHeaders();
    const url = ApiService.substitute(endpoints.update, {
      performerName: encodeURIComponent(username),
      identityType: encodeURIComponent(identityType),
    });

    return ApiService.put(url, { data, headers })
      .catch((error) => parseCatch(error));
  }

  createIdentity(
    username: Identity['username'],
    data: {
      name: string;
      birthdate: moment.Moment;
      aliases?: string;
    }
  ) {
    const headers = {};
    const url = ApiService.substitute(endpoints.create, { performerName: encodeURIComponent(username) });

    return ApiService.post(url, { data, headers })
      .then((result) => result.data)
      .catch((error) => parseCatch(error));
  }

  saveIdentityOnBoardingImage(
    username: Identity['username'],
    identityType: Identity['identityType'],
    data: {
      imageType: string;
      image: string;
    },
  ) {
    const url = ApiService.substitute(endpoints.image_upload, {
      performerName: encodeURIComponent(username),
      identityType: encodeURIComponent(identityType),
    });

    return ApiService.post(url, { data });
  }
}

export default new IdentitiesService();
